import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import startCase from 'lodash/startCase';
import { useHistory } from 'react-router-dom';
import { TooltipHost } from 'office-ui-fabric-react';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import { Stack } from 'office-ui-fabric-react/lib/Stack';
import { Text } from 'office-ui-fabric-react/lib/Text';
import styles from './AutosuggestContractItem.style';
import appStyles from '../../App.style';
import { contractStatus } from '../../utils';

const AutosuggestContractItem = ({
  index,
  href,
  onClick,
  contract,
  value,
  label,
  secondaryLabel,
  tertiaryLabel,
  approvalStatus,
  ...other
}) => {
  const history = useHistory();
  const status = contractStatus(
    contract.effectiveDate,
    contract.terminationDate,
    contract.approvalStatus,
  );

  const handleOnClick = () => {
    if (onClick) {
      onClick({ value, label, contract });
    } else if (history && href) {
      history.push(href);
    }
  };

  return (
    <Stack
      key={contract.contractId}
      className={styles.root}
      horizontal
      nowrap
      horizontalAlign="start"
      verticalAlign="center"
      tokens={{ childrenGap: 5 }}
      onClick={handleOnClick}
      onKeyPress={handleOnClick}
      tabIndex={0}
      {...other}
    >
      {approvalStatus && (
        <Stack.Item grow={0} shrink={0} className={styles.iconContainer}>
          <>
            {approvalStatus !== 'Approved' && (
              <TooltipHost
                content="Action Required"
                calloutProps={{
                  gapSpace: 0,
                  target: `#${`actionIcon${index}`}`,
                }}
              >
                <div className={styles.actionIndicator}>
                  <FontIcon
                    iconName="StatusCircleExclamation"
                    ariaLabel="TextDocument"
                    id={`actionIcon${index}`}
                  />
                </div>
              </TooltipHost>
            )}

            <TooltipHost
              content={startCase(status)}
              calloutProps={{
                gapSpace: 0,
                target: `#${`statusIcon${index}`}`,
              }}
            >
              <div className={clsx(styles.iconColumn, appStyles[`${status}Color`])}>
                <FontIcon
                  iconName="LocationDot"
                  ariaLabel="TextDocument"
                  id={`statusIcon${index}`}
                />
              </div>
            </TooltipHost>
          </>
        </Stack.Item>
      )}

      <Stack.Item grow={1} shrink={1} className={styles.labelContainer}>
        <Text className={styles.label}>{label}</Text>
      </Stack.Item>

      {secondaryLabel && (
        <>
          <Stack.Item grow={0} shrink={0} className={styles.secondaryLabelContainer}>
            <Text
              aria-label={`#${secondaryLabel} `}
              role="link"
              className={styles.description}
            >{`#${secondaryLabel}`}</Text>
          </Stack.Item>

          {tertiaryLabel && (
            <Stack.Item grow={0} shrink={0} className={styles.tertiaryLabelContainer}>
              <Text
                aria-label={`${tertiaryLabel}`}
                role="link"
                className={styles.description}
              >{`${tertiaryLabel}`}</Text>
            </Stack.Item>
          )}
        </>
      )}
    </Stack>
  );
};

AutosuggestContractItem.propTypes = {
  index: PropTypes.number.isRequired,
  href: PropTypes.string.isRequired,
  onClick: () => {},
  contract: PropTypes.shape({
    contractId: PropTypes.string.isRequired,
    effectiveDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
    terminationDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
    approvalStatus: PropTypes.string.isRequired,
  }).isRequired,
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  secondaryLabel: PropTypes.string.isRequired,
  tertiaryLabel: PropTypes.string.isRequired,
  approvalStatus: PropTypes.string.isRequired,
};

AutosuggestContractItem.defaultProps = {
  onClick: null,
};

export default AutosuggestContractItem;
